// src/appbuilder/digital-twin-helpers.js
//
// 共享的 "我的数字分身" 扫描 + 字段提取工具。
// 三个 clone 入口都调用这里：
//   1. 右下角 "Clone Your Digital Twin" pill (CloneDigitalTwinPanel)
//   2. Inbox Add icon → Clone (chat.js)
//   3. Profile icon → 绿色 Clone 按钮 (UserAppProfileLogin.js)
//
// 不用 collectionGroup("listings") + where authorUid（需要单独 index），
// 改成两步法：先拉所有已发布 app，再 per-app 单独 query listings。
// 单 collection query 不需要 collectionGroup index。

import {
  collection,
  collectionGroup,
  getDocs,
  query,
  where,
  limit as fsLimit,
} from "firebase/firestore";

const APPS_SCAN_LIMIT = 160;
const PER_APP_LISTING_LIMIT = 32;

function toMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return Number.isFinite(value) ? value : 0;
  if (typeof value?.toMillis === "function") {
    try { return Number(value.toMillis()) || 0; } catch { return 0; }
  }
  if (typeof value?.toDate === "function") {
    try { return Number(value.toDate().getTime()) || 0; } catch { return 0; }
  }
  if (typeof value === "string") {
    const t = Date.parse(value);
    return Number.isFinite(t) ? t : 0;
  }
  return 0;
}

function pickPhotoUrl(input) {
  if (!input) return "";
  if (typeof input === "string") return input.trim();
  if (Array.isArray(input)) {
    for (const p of input) {
      const u = pickPhotoUrl(p);
      if (u) return u;
    }
    return "";
  }
  if (typeof input === "object") {
    const u =
      input.url ||
      input.src ||
      input.downloadURL ||
      input.thumb ||
      input.thumbUrl;
    return typeof u === "string" ? u.trim() : "";
  }
  return "";
}

export function pickListingAvatar(row = {}, dataObj = {}) {
  return String(
    dataObj?.avatarUrl ||
      dataObj?.photoUrl ||
      dataObj?.photoURL ||
      dataObj?.imageUrl ||
      dataObj?.profilePhoto ||
      dataObj?.profileImage ||
      dataObj?.profilePic ||
      dataObj?.avatar ||
      pickPhotoUrl(dataObj?.coverPhoto) ||
      pickPhotoUrl(dataObj?.photos) ||
      row?.avatarUrl ||
      row?.photoUrl ||
      row?.photoURL ||
      row?.imageUrl ||
      row?.profilePhoto ||
      row?.profileImage ||
      row?.profilePic ||
      row?.avatar ||
      pickPhotoUrl(row?.coverPhoto) ||
      pickPhotoUrl(row?.photos) ||
      ""
  ).trim();
}

export function pickListingName(row = {}, dataObj = {}) {
  return String(
    dataObj?.title ||
      dataObj?.name ||
      dataObj?.displayName ||
      row?.title ||
      row?.name ||
      "Profile"
  ).trim();
}

function inferTemplateLabel(templateId) {
  const id = String(templateId || "").toLowerCase();
  if (id.includes("dating")) return "Dating";
  if (id.includes("housing") || id.includes("rent")) return "Housing";
  if (id.includes("job")) return "Jobs";
  if (id.includes("event")) return "Events";
  if (id.includes("sport")) return "Sports";
  if (id.includes("food")) return "Food";
  if (id.includes("market") || id.includes("shop")) return "Marketplace";
  if (id) return id.charAt(0).toUpperCase() + id.slice(1);
  return "App";
}

/**
 * 扫描当前用户在所有 Lex app 下创建过的 listings (digital twins)。
 *
 * @param {Object} args
 * @param {import("firebase/firestore").Firestore} args.db
 * @param {string} args.viewerUid          - 当前登录用户 uid
 * @param {string} [args.currentAppId]     - 当前 app ID（结果中会跳过它）
 * @returns {Promise<Array>}                  Twin objects (见下方 shape)
 *
 * Twin shape:
 *   {
 *     key, ownerUid, appId, appName, appLabel,
 *     listingId, templateId, templateLabel,
 *     name, avatar,
 *     listingData,    // 完整 source data，clone 时会拷贝它
 *     updatedAtMs,
 *     path,
 *   }
 */
export async function scanMyDigitalTwins({ db, viewerUid, currentAppId } = {}) {
  if (!db || !viewerUid) return [];

  // Step 1 — 拉所有已发布的 app（已存在 index）
  const appsSnap = await getDocs(
    query(
      collectionGroup(db, "apps"),
      where("published", "in", [true, "true"]),
      fsLimit(APPS_SCAN_LIMIT)
    )
  );

  const appRows = appsSnap.docs
    .map((d) => {
      const data = d.data() || {};
      const segs = String(d.ref.path || "").split("/");
      if (segs.length < 4 || segs[0] !== "users" || segs[2] !== "apps") {
        return null;
      }
      return {
        ownerUid: String(segs[1] || ""),
        appId: String(segs[3] || ""),
        appName: String(
          data.name || data.title || data.appName || segs[3] || "App"
        ).trim(),
        templateId: String(data.templateId || data.template || ""),
      };
    })
    .filter((x) => x && x.ownerUid && x.appId);

  // 跳过当前 app
  const candidates = appRows.filter(
    (x) => !(currentAppId && x.appId === String(currentAppId))
  );

  // Step 2 — per-app 单独 query listings where authorUid == viewerUid
  // 单 collection query，不需要 collectionGroup index
  const perAppRows = await Promise.all(
    candidates.map(async (appRow) => {
      try {
        const snap = await getDocs(
          query(
            collection(
              db,
              "users",
              appRow.ownerUid,
              "apps",
              appRow.appId,
              "listings"
            ),
            where("authorUid", "==", viewerUid),
            fsLimit(PER_APP_LISTING_LIMIT)
          )
        );
        if (!snap || snap.empty) return [];
        return snap.docs.map((d) => {
          const row = d.data() || {};
          const dataObj =
            row?.data && typeof row.data === "object" ? row.data : {};
          const listingId = String(d.id || "");
          const templateId =
            String(row?.templateId || appRow.templateId || "dating");
          return {
            key: `${appRow.ownerUid}__${appRow.appId}__${listingId}`,
            ownerUid: appRow.ownerUid,
            appId: appRow.appId,
            appName: appRow.appName,
            appLabel: appRow.appName || appRow.appId,
            listingId,
            templateId,
            templateLabel: inferTemplateLabel(templateId),
            name: pickListingName(row, dataObj),
            avatar: pickListingAvatar(row, dataObj),
            listingData: dataObj,
            updatedAtMs:
              toMillis(row.updatedAt) ||
              toMillis(row.lastUpdatedAt) ||
              toMillis(row.createdAt) ||
              0,
            path: `users/${appRow.ownerUid}/apps/${appRow.appId}/listings/${listingId}`,
          };
        });
      } catch {
        return [];
      }
    })
  );

  const flat = perAppRows.flat();

  // 去重：同 (ownerUid, appId) 取最新一条
  const dedup = new Map();
  for (const r of flat) {
    const k = `${r.ownerUid}::${r.appId}`;
    const prev = dedup.get(k);
    if (!prev || r.updatedAtMs > prev.updatedAtMs) dedup.set(k, r);
  }
  return Array.from(dedup.values()).sort(
    (a, b) => b.updatedAtMs - a.updatedAtMs
  );
}
